import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Upload, ConfigProvider, message } from 'antd';
import { ImagePlus, X as CloseIcon } from 'lucide-react';
import type { RcFile } from 'antd/es/upload';

const BRAND = '#066BFF';

const toBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

interface Props {
  open: boolean;
  initial: { title: string; content: string; imageUrl?: string };
  onClose: () => void;
  onSave: (patch: { title: string; content: string; imageUrl?: string }) => void;
}

export const EditContentModal: React.FC<Props> = ({
  open,
  initial,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState(initial.title);
  const [content, setContent] = useState(initial.content);
  const [imageUrl, setImageUrl] = useState<string | undefined>(initial.imageUrl);

  useEffect(() => {
    if (open) {
      setTitle(initial.title);
      setContent(initial.content);
      setImageUrl(initial.imageUrl);
    }
  }, [open, initial]);

  const save = () => {
    if (!title.trim() || !content.trim()) {
      message.error('Nhập đủ tiêu đề và nội dung');
      return;
    }
    onSave({ title: title.trim(), content: content.trim(), imageUrl });
    onClose();
  };

  return (
    <ConfigProvider
      theme={{ token: { fontFamily: "'Be Vietnam Pro', inter, sans-serif" } }}
    >
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        centered
        closeIcon={<CloseIcon size={20} className="text-slate-400" />}
        styles={{ content: { borderRadius: 20, padding: 24 }, body: { paddingTop: 8 } }}
      >
        <h2 className="text-center text-[24px] font-semibold leading-8 tracking-tight mb-5">
          Chỉnh sửa nội dung
        </h2>

        <div className="flex flex-col gap-2 mb-4">
          <span className="text-[14px] font-medium text-[#212b4e]">Tiêu đề chiến dịch</span>
          <Input
            size="large"
            className="!rounded-[12px]"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="Nhập tiêu đề"
          />
        </div>

        <div className="flex flex-col gap-2 mb-4">
          <span className="text-[14px] font-medium text-[#212b4e]">Nội dung chiến dịch</span>
          <Input.TextArea
            className="!rounded-[12px]"
            rows={5}
            maxLength={2000}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Mô tả mục đích, câu chuyện của chiến dịch..."
            style={{ resize: 'vertical' }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[14px] font-medium text-[#212b4e]">Hình ảnh / Video</span>
          <Upload
            accept="image/*,video/*"
            showUploadList={false}
            className="w-full [&_.ant-upload]:!w-full [&_.ant-upload-select]:!w-full"
            beforeUpload={async (file) => {
              if (file.size / 1024 / 1024 > 10) {
                message.error('Mỗi ảnh không quá 10mb');
                return Upload.LIST_IGNORE;
              }
              setImageUrl(await toBase64(file));
              return false;
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="preview"
                className="w-full max-h-[180px] object-cover rounded-[8px] border border-[#d1cfd2]"
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-1 h-[120px] w-full rounded-[8px] border border-dashed border-[#d1cfd2] cursor-pointer hover:border-[#066BFF] transition-colors">
                <ImagePlus size={24} className="text-[#066BFF]" />
                <span className="text-[14px]" style={{ color: BRAND }}>
                  Upload ảnh/video
                </span>
              </div>
            )}
          </Upload>
        </div>

        <div className="flex justify-center mt-5">
          <Button
            type="primary"
            size="large"
            onClick={save}
            className="!rounded-full !px-8"
            style={{ background: BRAND, borderColor: BRAND, fontFamily: 'inter, sans-serif' }}
          >
            Lưu
          </Button>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default EditContentModal;
